import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Inject,
  Input,
  OnInit,
  Optional,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';

import { MatCalendar, MatCalendarCellCssClasses } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';

import { DateTimeAdapter } from '../adapters/date-time-adapter';
import { NkDateTimeBase } from '../base/time-edit-base';

import { NK_TIME_EDIT_DEFAULT_OPTIONS, NkTimeEdit, NkTimeEditConfig } from '../time-edit/time-edit';

@Component({
  selector: 'nk-datetime-edit',
  templateUrl: 'datetime-edit.html',
  styleUrls: ['datetime-edit.scss', 'datetime-edit-picker.scss'],
  host: {
    class: 'nk-datetime'
  },
  exportAs: 'nkDateTimeEdit',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NkDateTimeEdit),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NkDateTimeEdit),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NkDateTimeEdit<D> extends NkDateTimeBase<D> implements OnInit, AfterViewInit {
  _minCal: D;
  _maxCal: D;

  /** show/hide the time edit part, transforming the component in a simple datepicker*/
  @Input() hideTime;

  /** A date representing the period (month or year) to start the calendar in. */
  @Input() startAt: D | null;

  /** Function used to filter which dates are selectable. */
  @Input() dateFilter: (date: D) => boolean;

  /** Function that can be used to add custom CSS classes to dates. */
  @Input() dateClass: (date: D) => MatCalendarCellCssClasses;

  @Input() defaultTime: string | null;

  @ViewChild(MatCalendar) calendar: MatCalendar<D>;
  @ViewChild(NkTimeEdit) timeEdit: NkTimeEdit<D>;

  constructor(
    public _dtAdapter: DateTimeAdapter<D>,
    @Optional()
    @Inject(NK_TIME_EDIT_DEFAULT_OPTIONS)
    public config: NkTimeEditConfig,
    public _changeDetectorRef: ChangeDetectorRef,
    @Optional() @Inject(MAT_DATE_FORMATS) _dateFormats: MatDateFormats
  ) {
    super(_dtAdapter, config, _changeDetectorRef);
  }

  setDate(ev) {
    if (this.disabled) {
      return;
    }
    let dt = ev;
    if (this.value) {
      dt = this._dtAdapter.setTimeFromDate(ev, this.value);
    } else {
      if (this.defaultTime) {
        const time = this.defaultTime.split(':');
        dt = this._dtAdapter.setTime(
          ev,
          time[0],
          time[1],
          this.showSeconds ? time[2] : 0,
          this.showMilliseconds ? time[3] : 0
        );
      }
    }

    const newV = this.clamp ? this._dtAdapter.clampDate(dt, this.min, this.max) : dt;

    if (!this._dtAdapter.sameDateTime(newV, this.value)) {
      this.value = newV;
    }
    this._onUserInput(this.value);
  }

  updateControls = (v: D) => {
    if (v) {
      if (this.calendar) {
        this.calendar._goToDateInView(v, 'month');
      }
      //      this.timeEdit.updateControls(v);
    }
    if (!this.min || !v) {
      this._minCal = this.min;
    } else {
      if (v > this._dtAdapter.setTimeFromDate(v, this.min)) {
        this._minCal = this.min;
      } else {
        this._minCal = this._dtAdapter.addCalendarDays(this.min, 1);
      }
    }

    if (!this.max || !v) {
      this._maxCal = this.max;
    } else {
      if (v < this._dtAdapter.setTimeFromDate(v, this.max)) {
        this._maxCal = this.max;
      } else {
        this._maxCal = this._dtAdapter.addCalendarDays(this.max, -1);
      }
    }
    this._changeDetectorRef.markForCheck();
  };

  ngOnInit(): void {
    this.config = this.config || {};
    this.showMeridian =
      this.showMeridian === void 0 ? this.config.showMeridian : coerceBooleanProperty(this.showMeridian);
    this.showSeconds = this.showSeconds === void 0 ? this.config.showSeconds : coerceBooleanProperty(this.showSeconds);
    this.showMilliseconds =
      this.showMilliseconds === void 0 ? this.config.showMilliseconds : coerceBooleanProperty(this.showMilliseconds);

    this._minCal = this.min;
    this._maxCal = this.max;
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    this.timeEdit.validStateChange.subscribe((errs: ValidationErrors) => {
      this._validationErrors = errs;
      this._onValidatorChange();
    });
  }

  validate(c: AbstractControl): ValidationErrors | null {
    this._validationErrors = (this.timeEdit && this.timeEdit._validationErrors) || super.validate(c);
    return this._validationErrors;
  }
}
